import { useState, type ReactNode } from 'react';

interface BaseProps {
  label: string;
  help?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
}

export function Field({ label, help, error, required, children, htmlFor }: BaseProps) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
        {required && <span className="field__required" aria-hidden> *</span>}
      </label>
      {children}
      {error ? (
        <span className="field__error">{error}</span>
      ) : help ? (
        <span className="field__help">{help}</span>
      ) : null}
    </div>
  );
}

interface TextProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  type?: 'text' | 'date' | 'time' | 'datetime-local' | 'email' | 'tel' | 'color' | 'password';
  disabled?: boolean;
  autoComplete?: string;
}

export function TextInput({ id, label, value, onChange, type = 'text', ...rest }: TextProps) {
  return (
    <Field label={label} htmlFor={id} {...rest}>
      <input
        id={id}
        type={type}
        value={value ?? ''}
        placeholder={rest.placeholder}
        disabled={rest.disabled}
        // A password field must never offer to fill in the administrator's
        // own saved credentials, nor invite the browser to store the one
        // being typed for someone else.
        autoComplete={rest.autoComplete ?? (type === 'password' ? 'new-password' : undefined)}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function TextArea({ id, label, value, onChange, ...rest }: TextProps) {
  return (
    <Field label={label} htmlFor={id} {...rest}>
      <textarea
        id={id}
        value={value ?? ''}
        placeholder={rest.placeholder}
        disabled={rest.disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

interface DateTimeProps {
  id: string;
  label: string;
  /** ISO 8601, or null. */
  value: string | null;
  onChange: (iso: string | null) => void;
  help?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/**
 * A datetime-local input that keeps what is being typed.
 *
 * The browser reports an empty value while a date is half-entered — change
 * the year and for one keystroke the whole field is incomplete. Feeding that
 * straight back as `null` re-renders the input blank and wipes the segments
 * the person had already filled in. So the raw text lives here, and the model
 * is only told about a value that actually parses.
 */
export function DateTimeInput({
  id, label, value, onChange, ...rest
}: DateTimeProps) {
  const [raw, setRaw] = useState(() => toLocalInput(value));
  const [lastValue, setLastValue] = useState(value);

  // Follow the model when it changes from outside (a different record loaded),
  // but never while the person is mid-edit.
  if (value !== lastValue) {
    setLastValue(value);
    setRaw(toLocalInput(value));
  }

  return (
    <Field label={label} htmlFor={id} {...rest}>
      <input
        id={id}
        type="datetime-local"
        value={raw}
        disabled={rest.disabled}
        onChange={(e) => {
          const next = e.target.value;
          setRaw(next);
          const parsed = next ? new Date(next) : null;
          if (!next) {
            setLastValue(null);
            onChange(null);
          } else if (parsed && !Number.isNaN(parsed.getTime())) {
            const iso = parsed.toISOString();
            setLastValue(iso);
            onChange(iso);
          }
          // A partial value is left alone: shown, but not yet committed.
        }}
      />
    </Field>
  );
}

interface NumberProps {
  id: string;
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  help?: string;
  error?: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  disabled?: boolean;
}

export function NumberInput({ id, label, value, onChange, suffix, ...rest }: NumberProps) {
  return (
    <Field label={label} htmlFor={id} {...rest}>
      <div className="input-group">
        <input
          id={id}
          type="number"
          className="tabular"
          value={value ?? ''}
          min={rest.min}
          max={rest.max}
          step={rest.step ?? 'any'}
          disabled={rest.disabled}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
        {suffix && <span className="input-group__suffix">{suffix}</span>}
      </div>
    </Field>
  );
}

interface SelectProps {
  id: string;
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  options: { value: string; label: string }[];
  help?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function SelectInput({
  id, label, value, onChange, options, placeholder = '— select —', ...rest
}: SelectProps) {
  return (
    <Field label={label} htmlFor={id} {...rest}>
      <select
        id={id}
        value={value ?? ''}
        disabled={rest.disabled}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}

interface CheckProps {
  id: string;
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  help?: string;
  disabled?: boolean;
}

export function CheckboxInput({ id, label, value, onChange, help, disabled }: CheckProps) {
  return (
    <div className="field field--check">
      <label className="check" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={!!value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{label}</span>
      </label>
      {help && <span className="field__help">{help}</span>}
    </div>
  );
}
